import {buildWorkbench,enrichWorkbench,loadWorkbench,saveWorkbench} from './researchWorkbench.js';
import {loadGrasshopperSnapshot,loadShortInterestSnapshot,loadDemandEvidence,loadMarketEvents,loadCalendarItems,loadCompanyFacts,loadAlphaCompany,loadConsensus,loadSecForm4,loadEcosystemNeighborhood,loadSecTrajectory,getCurrentUser} from './liveData.js';
import {loadPrivateWorkspace} from './privateWorkspace.js';
import {ECOSYSTEM_VERSION,buildEcosystemAnalysis} from './ecosystemResearch.js';
import {loadCachedMarket} from './cachedMarket.js';
import {persistVerifiedResearch} from './researchPersistence.js';
import {deriveSecFinancials} from './secFinancials.js';
import {deriveCompanyQualityEvidence} from './companyQualityEvidence.js';
import {buildResearchCoverage} from './researchCoverage.js';
import {deriveInsiderEvidence} from './insiderEvidence.js';
import {deriveTenXResearchEvidence} from './tenXResearch.js';
import {deriveMoatEvidence} from './moatEvidence.js';
import {deriveFoxEvidence} from './foxEvidence.js';
import {deriveRocketEvidence} from './rocketEvidence.js';
import {deriveMoleEvidence} from './moleEvidence.js';
import {deriveGrasshopperEvidence} from './grasshopperEvidence.js';
import {deriveSheepEvidence} from './sheepEvidence.js';
import {deriveDogEvidence} from './dogEvidence.js';
import {deriveStockOpportunityEvidence} from './stockOpportunityEvidence.js';
import {deriveWhyWrongEvidence} from './whyWrongEvidence.js';
import {persistScreeningSummary} from './researchShortlist.js';
import {deriveResearchState} from './researchState.js';

const inflight=new Map();

async function loadBundle(symbol){
  const [grasshopper,shortInterest,demand,events,calendar,market,companyFacts,alphaCompany,consensus,ecosystemNeighborhood]=await Promise.all([
    loadGrasshopperSnapshot(symbol).catch(()=>null),
    loadShortInterestSnapshot(symbol).catch(()=>null),
    loadDemandEvidence(symbol).catch(()=>[]),
    loadMarketEvents(symbol).catch(()=>[]),
    loadCalendarItems(symbol).catch(()=>[]),
    loadCachedMarket(symbol).catch(e=>({status:'REVIEW',error:e.message,price:null,rows:[],cache:'MISS'})),
    loadCompanyFacts(symbol).catch(e=>({status:'REVIEW',error:e.message,companyName:null,cik:null})),
    loadAlphaCompany(symbol).catch(e=>({status:'REVIEW',error:e.message,company:null})),
    loadConsensus(symbol).catch(e=>({status:'REVIEW',error:e.message,snapshot:null,diagnostics:null})),
    loadEcosystemNeighborhood(symbol).catch(e=>({relationships:[],rows:[],depth:0,truncated:false,error:e.message}))
  ]);
  const secFinancials=companyFacts?.status==='VERIFIED'&&companyFacts?.payload?deriveSecFinancials(companyFacts.payload):{status:'MISSING',metrics:{},source:'SEC Company Facts'};
  const companyQualityEvidence=deriveCompanyQualityEvidence(secFinancials);
  const [form4,secTrajectory]=companyFacts?.cik?await Promise.all([
    loadSecForm4(symbol,companyFacts.cik).catch(e=>({status:'REVIEW',symbol,cik:companyFacts.cik,transactions:[],filings_scanned:0,filing_failures:0,error:e?.message||'SEC_FORM4_REVIEW'})),
    loadSecTrajectory(symbol,companyFacts.cik).catch(e=>({status:'REVIEW',metrics:[],mentions:[],error:e.message}))
  ]):[{status:'MISSING',symbol,transactions:[],filings_scanned:0,filing_failures:0},{status:'MISSING',metrics:[],mentions:[]}];
  const insiderEvidence=deriveInsiderEvidence(form4);
  const relationships=ecosystemNeighborhood.relationships;
  const draft={ecosystemNeighborhood,grasshopper,shortInterest,demand,events,calendar,market,companyFacts,alphaCompany,consensus,relationships,secFinancials,companyQualityEvidence,form4,insiderEvidence,secTrajectory};
  const tenXResearch=deriveTenXResearchEvidence(draft);
  const moatEvidence=deriveMoatEvidence(draft);
  const foxEvidence=deriveFoxEvidence({...draft,tenXResearch});
  const rocketEvidence=deriveRocketEvidence(draft);
  const moleEvidence=deriveMoleEvidence(draft);
  const grasshopperEvidence=deriveGrasshopperEvidence(draft);
  const sheepEvidence=deriveSheepEvidence(draft);
  const dogEvidence=deriveDogEvidence({...draft,moatEvidence});
  const stockOpportunityEvidence=deriveStockOpportunityEvidence({tenXResearch,foxEvidence,rocketEvidence,sheepEvidence,moleEvidence,grasshopperEvidence});
  const whyWrongEvidence=deriveWhyWrongEvidence({...draft,dogEvidence,rocketEvidence,moleEvidence,sheepEvidence});
  const bundle={...draft,tenXResearch,moatEvidence,foxEvidence,rocketEvidence,moleEvidence,grasshopperEvidence,sheepEvidence,dogEvidence,stockOpportunityEvidence,whyWrongEvidence};
  try{bundle.ecosystemPrivate=(await loadPrivateWorkspace(`ecosystem:${symbol}`,ECOSYSTEM_VERSION))?.payload||null;}catch(e){bundle.ecosystemPrivateError=e.message;}
  bundle.ecosystemAnalysis=buildEcosystemAnalysis(symbol,bundle,bundle.ecosystemPrivate);
  bundle.coverage=buildResearchCoverage(bundle);
  bundle.researchStateEvidence=deriveResearchState(bundle);
  return bundle;
}

export async function runResearch(ticker,{persist=true}={}){
  const symbol=String(ticker||'').trim().toUpperCase();
  if(!/^[A-Z0-9.\-]{1,12}$/.test(symbol))throw new Error('VALID_SYMBOL_REQUIRED');
  const owner=await getCurrentUser();if(!owner)throw new Error('Authentication required');
  const jobKey=`${owner.id}:${symbol}`;
  if(inflight.has(jobKey))return inflight.get(jobKey);
  const job=(async()=>{
    const startedAt=new Date().toISOString();
    const bundle=await loadBundle(symbol);
    if((await getCurrentUser())?.id!==owner.id)throw new Error('AUTH_SESSION_CHANGED');
    const verifiedMarket=bundle?.market?.status==='VERIFIED';
    const verifiedCompany=bundle?.companyFacts?.status==='VERIFIED'||bundle?.alphaCompany?.status==='VERIFIED';
    const shouldPersist=persist&&((verifiedMarket&&bundle.market.cache!=='HIT')||verifiedCompany);
    const persistence=shouldPersist?await persistVerifiedResearch(bundle):{company:null,market:null,errors:[]};
    const researchState=bundle?.researchStateEvidence?.state||'REVIEW';
    const completedAt=new Date().toISOString();
    let previous=null;
    try{previous=await loadWorkbench(symbol)}catch(e){persistence.errors.push('RESEARCH_MEMORY_LOAD_FAILED')}
    bundle.workbench=await enrichWorkbench(buildWorkbench(symbol,bundle,previous?.payload||null,completedAt),previous?.payload||null);
    if(persist){try{persistence.workbench=await saveWorkbench(bundle.workbench,previous,undefined,owner.id)}catch(e){persistence.errors.push(e?.message||'RESEARCH_MEMORY_SAVE_FAILED')}}
    if(persist){
      try{persistence.screening=await persistScreeningSummary(symbol,bundle,researchState,completedAt)}
      catch(e){persistence.screening=null;persistence.errors=[...(persistence.errors||[]),e?.message||'SCREENING_SUMMARY_SAVE_FAILED']}
    }
    if((await getCurrentUser())?.id!==owner.id)throw new Error('AUTH_SESSION_CHANGED');
    return{ticker:symbol,researchState,startedAt,completedAt,bundle,persistence,coverage:bundle.coverage};
  })();
  inflight.set(jobKey,job);
  try{return await job}finally{inflight.delete(jobKey)}
}

export async function applyEcosystemResearch(symbol,bundle,privateState,expectedUserId){
 const owner=await getCurrentUser();if(!owner||owner.id!==expectedUserId)throw new Error('AUTH_SESSION_CHANGED');
 if(bundle.workbench?.symbol&&bundle.workbench.symbol!==symbol)throw new Error('ECOSYSTEM_SYMBOL_MISMATCH');
 const next={...bundle,ecosystemPrivate:privateState,ecosystemAnalysis:buildEcosystemAnalysis(symbol,bundle,privateState)};
 // Prior restored evidence remains visible while a fresh provider run is loading.
 if(!bundle.workbench||bundle.workbench.restored)return next;
 const previous=await loadWorkbench(symbol);
 next.workbench=await enrichWorkbench(buildWorkbench(symbol,next,previous?.payload||null),previous?.payload||null);
 await saveWorkbench(next.workbench,previous,undefined,owner.id);
 if((await getCurrentUser())?.id!==owner.id)throw new Error('AUTH_SESSION_CHANGED');return next;
}
